import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSignalNotificationStore } from '../../store/signalNotificationStore';
import type { SignalNotificationItem } from '../../services/signalNotifications.service';
import { playNewSignalSound } from '../../lib/notificationSound';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format a UTC date string as a human-readable time-ago string (English only
 * for now — the relative format is intentionally neutral: "5m", "2h", "3d").
 */
function timeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/** Cap badge value at "9+" */
function badgeLabel(count: number): string {
  return count > 9 ? '9+' : String(count);
}

// ---------------------------------------------------------------------------
// Notification row
// ---------------------------------------------------------------------------

interface NotificationRowProps {
  item: SignalNotificationItem;
  onRowClick: (item: SignalNotificationItem) => void;
}

function NotificationRow({ item, onRowClick }: NotificationRowProps) {
  const isUnread = item.viewedAt === null;
  const isLong = item.signal.direction === 'LONG';

  return (
    <button
      type="button"
      onClick={() => onRowClick(item)}
      className={`
        w-full flex items-start gap-3 px-4 py-3 text-left
        hover:bg-bg-tertiary transition-colors focus-visible:outline-none
        focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-inset
        ${isUnread ? 'bg-bg-tertiary/40' : ''}
      `}
    >
      {/* Unread accent bar */}
      <span
        className={`
          mt-1 w-1 h-full min-h-[16px] rounded-full shrink-0 self-stretch
          ${isUnread ? 'bg-accent-primary' : 'bg-transparent'}
        `}
        aria-hidden="true"
      />

      <div className="flex-1 min-w-0">
        {/* Asset + direction badge + timestamp */}
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-text-primary truncate">
            {item.signal.asset}
          </span>
          {/* Direction badge — only profit/loss colors as per sacred-colors rule */}
          <span
            className={`
              inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold
              ${
                isLong
                  ? 'bg-profit/15 text-profit'
                  : 'bg-loss/15 text-loss'
              }
            `}
          >
            {item.signal.direction}
          </span>
          <span className="ml-auto text-[11px] text-text-tertiary shrink-0">
            {timeAgo(item.signal.createdAt)}
          </span>
        </div>

        {/* Confidence */}
        <p className="text-xs text-text-secondary mb-0.5">
          {item.signal.confidence}% confidence
        </p>

        {/* Narrative excerpt (max 2 lines) */}
        {item.signal.narrative && (
          <p className="text-xs text-text-tertiary line-clamp-2">
            {item.signal.narrative}
          </p>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Dropdown panel
// ---------------------------------------------------------------------------

interface DropdownPanelProps {
  onClose: () => void;
  isMobile: boolean;
}

function DropdownPanel({ onClose, isMobile }: DropdownPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, isLoading, markRead, markAllRead, loadRecent, unreadCount } =
    useSignalNotificationStore();

  // Load recent on first open
  useEffect(() => {
    void loadRecent(10);
  }, [loadRecent]);

  const handleRowClick = useCallback(
    async (item: SignalNotificationItem) => {
      if (item.viewedAt === null) {
        await markRead(item.id);
      }
      onClose();
      navigate('/signals');
    },
    [markRead, navigate, onClose]
  );

  const handleMarkAll = useCallback(async () => {
    await markAllRead();
  }, [markAllRead]);

  return (
    <div
      role="dialog"
      aria-label={t('notifications.bell.title')}
      className={`
        ${
          isMobile
            ? // Mobile: full-width bottom sheet
              'fixed inset-x-0 bottom-0 z-50 rounded-t-2xl max-h-[75vh] flex flex-col'
            : // Desktop: positioned dropdown
              'absolute right-0 mt-2 w-96 rounded-xl z-50 max-h-[80vh] flex flex-col'
        }
        bg-bg-elevated border border-border-default shadow-xl overflow-hidden animate-fade-in
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle shrink-0">
        <span className="text-sm font-semibold text-text-primary">
          {t('notifications.bell.title')}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAll}
            className="text-xs text-accent-primary hover:text-accent-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded"
          >
            {t('notifications.bell.markAll')}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-24 text-sm text-text-tertiary">
            {t('common.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-text-tertiary">
            <Bell className="w-8 h-8 opacity-40" aria-hidden="true" />
            <p className="text-sm">{t('notifications.bell.empty')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border-subtle/50">
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onRowClick={handleRowClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div className="border-t border-border-subtle shrink-0 px-4 py-2.5">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/signals');
            }}
            className="w-full text-sm text-center text-accent-primary hover:text-accent-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded py-0.5"
          >
            {t('notifications.bell.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bell button + mobile overlay
// ---------------------------------------------------------------------------

const REFRESH_INTERVAL_MS = 60_000; // fallback until Wave2-G useSignalStream integration

export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const { unreadCount, loadUnreadCount } = useSignalNotificationStore();

  // Play the new-signal chime whenever the unread count *increases* (i.e. a
  // new signal arrived). Skipping the very first observed value avoids
  // chiming the user the moment they open the app with existing unread
  // notifications. The ref persists across renders without re-triggering
  // the effect itself.
  const previousUnreadRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = previousUnreadRef.current;
    if (prev !== null && unreadCount > prev) {
      playNewSignalSound();
    }
    previousUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Detect mobile breakpoint (< 768px == md)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Initial load and window-focus refresh
  useEffect(() => {
    void loadUnreadCount();

    const onFocus = () => {
      void loadUnreadCount();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadUnreadCount]);

  // Polling fallback (60s interval).
  // TODO(Wave2-G): replace interval with useSignalStream `new_signal` callback
  // once the Wave2-G WebSocket integration lands. See feat/in-app-notification-bell PR.
  useEffect(() => {
    const id = setInterval(() => {
      void loadUnreadCount();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loadUnreadCount]);

  // Service worker push messages — triggered when the SW receives a push
  // notification and broadcasts `{ type: 'signal-received' }` to all clients.
  // This keeps the unread badge in sync even when the page is already open.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data?.type === 'signal-received') {
        void loadUnreadCount();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [loadUnreadCount]);

  // Click-outside-to-close (desktop dropdown)
  useEffect(() => {
    if (!open || isMobile) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isMobile]);

  const close = useCallback(() => setOpen(false), []);

  // The mobile bottom-sheet uses `position: fixed`, but the topbar has
  // `backdrop-blur-lg` (a `backdrop-filter`) which makes it the containing
  // block for any fixed descendants. Without a portal the sheet ends up
  // pinned to the 64-px topbar height instead of the viewport bottom. Render
  // both the backdrop and the sheet through a portal to escape that ancestor.
  const mobileSheet = open && isMobile
    ? createPortal(
        <>
          <div
            role="presentation"
            className="fixed inset-0 bg-black/50 z-40"
            onClick={close}
          />
          <DropdownPanel onClose={close} isMobile={true} />
        </>,
        document.body,
      )
    : null;

  return (
    <>
      <div ref={wrapperRef} className="relative">
        {/* Bell button */}
        <button
          ref={triggerRef}
          type="button"
          aria-label={t('notifications.bell.title')}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((o) => !o)}
          className="
            relative p-2 rounded-lg text-text-secondary
            hover:text-text-primary hover:bg-bg-tertiary
            transition-colors focus-visible:outline-none
            focus-visible:ring-2 focus-visible:ring-accent-primary
            min-w-[44px] min-h-[44px] flex items-center justify-center
          "
        >
          <Bell className="w-5 h-5" aria-hidden="true" />

          {/* Unread badge */}
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="
                absolute top-0.5 right-0.5
                min-w-[16px] h-4 px-0.5
                bg-accent-primary text-white
                text-[10px] font-bold leading-4
                rounded-full flex items-center justify-center
              "
            >
              {badgeLabel(unreadCount)}
            </span>
          )}
        </button>

        {/* Desktop dropdown stays inside the wrapper — it uses
            `position: absolute` so it's anchored to the bell. */}
        {open && !isMobile && (
          <DropdownPanel onClose={close} isMobile={false} />
        )}
      </div>

      {mobileSheet}
    </>
  );
}
